import { useEffect, useState } from 'react';
import { clsx } from 'clsx';

import { toast } from '../../Toast';
import { Info } from '../../Info/Info';
import { Table } from '../../Table/Table';
import type { TableColumn } from '../../Table/Table.types';
import { useMasterDocumentFormatters } from '../formatters';

import { WithholdingOriginBadge } from './WithholdingOriginBadge';
import { WithholdingTotalCell } from './WithholdingTotalCell';
import { WithholdingsActionBar } from './WithholdingsActionBar';
import { WithholdingsAddModal } from './WithholdingsAddModal';
import { WithholdingsEditModal } from './WithholdingsEditModal';
import { WithholdingsConfirmModal } from './WithholdingsConfirmModal';
import { useWithholdingsTranslation } from './i18n';
import {
  useWithholdings,
  useAddManualWithholding,
  useModifyWithholding,
  useDeleteWithholding,
  useRecalculateWithholdings,
} from './hooks';
import { getWithholdingOrigin } from './types';
import type {
  CreateWithholdingPayload,
  ModifyWithholdingPayload,
  WithholdingDto,
  WithholdingsHttpError,
  WithholdingsPanelProps,
} from './types';

const DASH = '—';

type ConfirmKind = 'delete' | 'recalculate' | null;

/**
 * Panel de Retenciones (TF3) — sub-componente de MasterDocument (FEAT-105-11).
 *
 * Renderiza la grilla TF3 del documento (Clase, Base, Tasa, Total, Acción y badge
 * de Origen). Cuando el usuario tiene permiso y el documento es editable (DRAFT)
 * muestra la barra de acciones: Agregar (POST), Modificar (PUT con `rowVersion`),
 * Eliminar (DELETE, solo manuales) y Recalcular (POST recalculate, preserva
 * manuales/manipuladas). Sin permiso o fuera de DRAFT la barra se oculta y la
 * grilla queda read-only.
 *
 * Diseñado para montarse en una sección FactBox de cualquier documento
 * MasterDocument, inyectando endpoints, IO y catálogos por props (precedente:
 * InstallmentsPanel).
 *
 * @example
 * ```tsx
 * <WithholdingsPanel
 *   documentId={docId}
 *   isEditable={docState === 'DRAFT'}
 *   canWrite={permissions.includes(CORE_PERMISSIONS.WITHHOLDINGS.WRITE)}
 *   canRecalculate={permissions.includes(CORE_PERMISSIONS.SUPPLIER_INVOICES.MODIFY_WITHHOLDINGS)}
 *   withholdingsEndpoint={`/api/v1/purchases/supplier-invoices/${docId}/withholdings`}
 *   withholdingsBaseEndpoint={`/api/v1/purchases/supplier-invoices/${docId}/withholdings`}
 *   httpRequest={httpRequest}
 *   catalogs={catalogs}
 * />
 * ```
 */
export function WithholdingsPanel({
  documentId,
  isEditable,
  canWrite,
  canRecalculate,
  withholdingsEndpoint,
  withholdingsBaseEndpoint,
  httpRequest,
  catalogs,
  initialWithholdings,
  formatAmount: formatAmountProp,
  className,
}: WithholdingsPanelProps) {
  const { t } = useWithholdingsTranslation();
  const { formatAmount: defaultFormatAmount } = useMasterDocumentFormatters();
  const formatAmount = formatAmountProp ?? defaultFormatAmount;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmKind>(null);

  const withholdingsQuery = useWithholdings({
    documentId,
    withholdingsEndpoint,
    httpRequest,
    initialData: initialWithholdings,
  });

  const addMutation = useAddManualWithholding({ documentId, withholdingsBaseEndpoint, httpRequest });
  const modifyMutation = useModifyWithholding({ documentId, withholdingsBaseEndpoint, httpRequest });
  const deleteMutation = useDeleteWithholding({ documentId, withholdingsBaseEndpoint, httpRequest });
  const recalcMutation = useRecalculateWithholdings({
    documentId,
    withholdingsBaseEndpoint,
    httpRequest,
  });

  const withholdings = withholdingsQuery.data ?? [];
  const selectedRow = withholdings.find((w) => w.id === selectedId) ?? null;

  // Si la fila seleccionada desaparece tras un refresco, limpiar la selección.
  useEffect(() => {
    if (selectedId && !(withholdingsQuery.data ?? []).some((w) => w.id === selectedId)) {
      setSelectedId(null);
    }
  }, [withholdingsQuery.data, selectedId]);

  const isBusy =
    addMutation.isPending ||
    modifyMutation.isPending ||
    deleteMutation.isPending ||
    recalcMutation.isPending;

  /** La barra se muestra solo en DRAFT y con algún permiso (oculta, no deshabilitada). */
  const showActionBar = isEditable && (canWrite || canRecalculate);

  function reportError(error: unknown) {
    const err = error as WithholdingsHttpError;
    const detail = err?.detail;
    switch (err?.status) {
      case 403:
        toast.error(t('withholdings.error.forbidden'));
        return;
      case 404:
        toast.error(detail || t('withholdings.error.notFound'));
        return;
      case 409:
        toast.error(detail || t('withholdings.error.conflict'));
        return;
      case 422:
        toast.error(detail || t('withholdings.error.validation'));
        return;
      case 503:
        toast.error(t('withholdings.error.liquitaxUnavailable'));
        return;
      default:
        toast.error(detail || err?.message || t('withholdings.error.generic'));
    }
  }

  function handleAdd(payload: CreateWithholdingPayload) {
    addMutation.mutate(payload, {
      onSuccess: () => {
        toast.success(t('withholdings.toast.added'));
        setAddOpen(false);
      },
      onError: reportError,
    });
  }

  function handleEdit(payload: ModifyWithholdingPayload) {
    if (!selectedRow) return;
    modifyMutation.mutate(
      { withholdingId: selectedRow.id, payload },
      {
        onSuccess: () => {
          toast.success(t('withholdings.toast.updated'));
          setEditOpen(false);
        },
        onError: reportError,
      },
    );
  }

  function handleDelete() {
    if (!selectedRow) return;
    deleteMutation.mutate(selectedRow.id, {
      onSuccess: () => {
        toast.success(t('withholdings.toast.deleted'));
        setConfirm(null);
        setSelectedId(null);
      },
      onError: reportError,
    });
  }

  function handleRecalculate() {
    recalcMutation.mutate(undefined, {
      onSuccess: (result) => {
        toast.success(
          t('withholdings.toast.recalculated', {
            preserved: result.summary.preserved,
            recalculated: result.summary.recalculated,
          }),
        );
        setConfirm(null);
      },
      onError: reportError,
    });
  }

  const columns: TableColumn<WithholdingDto>[] = [
    {
      header: t('withholdings.column.class'),
      accessor: 'withholdingClassName',
      render: (_value, row) => row.withholdingClassName || row.withholdingKeyCode || DASH,
    },
    {
      header: t('withholdings.column.base'),
      accessor: 'baseValue',
      align: 'right',
      render: (value) => <span className="tabular-nums">{formatAmount(value as number)}</span>,
    },
    {
      header: t('withholdings.column.rate'),
      accessor: 'rate',
      align: 'right',
      width: '72px',
      render: (value) => (value == null ? DASH : <span className="tabular-nums">{`${value}%`}</span>),
    },
    {
      header: t('withholdings.column.total'),
      accessor: 'totalValue',
      align: 'right',
      render: (_value, row) => <WithholdingTotalCell row={row} formatAmount={formatAmount} />,
    },
    {
      header: t('withholdings.column.action'),
      accessor: 'withholdingAction',
      align: 'center',
      render: (value) => {
        // El rango declarado es 1..4; cualquier otro valor del backend caeria en
        // la clave cruda, asi que se degrada a un texto legible.
        const clave = `withholdings.action.${value as number}`;
        const texto = t(clave);
        return texto === clave ? t('withholdings.action.unknown') : texto;
      },
    },
    {
      header: t('withholdings.column.origin'),
      accessor: 'isManual',
      align: 'center',
      width: '110px',
      render: (_value, row) => <WithholdingOriginBadge origin={getWithholdingOrigin(row)} />,
    },
  ];

  return (
    <section
      className={clsx('flex flex-col gap-3', className)}
      aria-label={t('withholdings.panel.title')}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-content-primary dark:text-dark-content-primary truncate">
          {t('withholdings.panel.title')}
        </h3>
      </div>

      {/* Barra de acciones (solo DRAFT + permiso) */}
      {showActionBar && (
        <WithholdingsActionBar
          canWrite={canWrite}
          canRecalculate={canRecalculate}
          hasCatalogs={Boolean(catalogs)}
          selectedRow={selectedRow}
          isBusy={isBusy}
          onAdd={() => setAddOpen(true)}
          onEdit={() => selectedRow && setEditOpen(true)}
          onDelete={() => selectedRow && setConfirm('delete')}
          onRecalculate={() => setConfirm('recalculate')}
        />
      )}

      {/* Estados de carga / error / grilla */}
      {withholdingsQuery.isError ? (
        <Info color="red" title={t('withholdings.panel.loadError')} />
      ) : (
        <Table<WithholdingDto>
          columns={columns}
          data={withholdings}
          variant="basic"
          fullWidth
          showShadow={false}
          loading={withholdingsQuery.isLoading}
          emptyMessage={t('withholdings.panel.empty')}
          onRowClick={
            showActionBar && canWrite
              ? (row) => setSelectedId((prev) => (prev === row.id ? null : row.id))
              : undefined
          }
          isRowSelected={(row) => row.id === selectedId}
        />
      )}

      {/* Modal Agregar (solo con permiso + catálogos) */}
      {showActionBar && canWrite && catalogs && (
        <WithholdingsAddModal
          open={addOpen}
          catalogs={catalogs}
          isSubmitting={addMutation.isPending}
          onSubmit={handleAdd}
          onClose={() => setAddOpen(false)}
        />
      )}

      {/* Modal Modificar */}
      {showActionBar && canWrite && (
        <WithholdingsEditModal
          open={editOpen}
          row={selectedRow}
          isSubmitting={modifyMutation.isPending}
          onSubmit={handleEdit}
          onClose={() => setEditOpen(false)}
        />
      )}

      {/* Confirmación Eliminar */}
      <WithholdingsConfirmModal
        open={confirm === 'delete'}
        type="danger"
        title={t('withholdings.delete.title')}
        body={t('withholdings.delete.body')}
        confirmLabel={t('withholdings.delete.confirm')}
        cancelLabel={t('withholdings.delete.cancel')}
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onClose={() => setConfirm(null)}
      />

      {/* Confirmación Recalcular */}
      <WithholdingsConfirmModal
        open={confirm === 'recalculate'}
        type="warning"
        title={t('withholdings.recalculate.title')}
        body={t('withholdings.recalculate.body')}
        confirmLabel={t('withholdings.recalculate.confirm')}
        cancelLabel={t('withholdings.recalculate.cancel')}
        isLoading={recalcMutation.isPending}
        onConfirm={handleRecalculate}
        onClose={() => setConfirm(null)}
      />
    </section>
  );
}
